/**
 * PotApprovalsPage
 * Main page for pot approvals workflow.
 * Pattern: Page as Orchestrator using PageContentArea + PageHeader + PageToolbar
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarMonthIcon, PageHeader } from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { getErrorMessage } from '@billda/shared-ui';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PotApprovalsListView } from '../components/PotApprovalsListView';
import { DeadlinesPanel } from '../components/DeadlinesPanel';
import { PageSnackbar } from '../components/PageSnackbar';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { POT_APPROVALS_HELP } from '../../../shared/constants/helpContent';
import { PeriodSelector } from '../components/PeriodSelector';
import { PotGroupingMenu } from '../components/PotGroupingMenu';
import { usePotPeriodState } from '../hooks/usePotPeriodState';
import { usePeriodNavigation } from '../hooks/usePeriodNavigation';
import { usePotApprovalsUIStore } from '../stores/potApprovalsUIStore';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { useGetPotPeriods } from '../hooks/useGetPotPeriods';
import { useGetPots } from '../hooks/useGetPots';
import { useMovePotToActivePeriod } from '../hooks/useMovePotToActivePeriod';
import { useMoveToPrimaryHandler } from '../hooks/useMoveToPrimary';
import { useExtendDeadlineAction } from '../hooks/useExtendDeadlineAction';
import { convertToTablePot } from '../utils/potTransformers';
import { useCurrentIsoWeekYear } from '../hooks/useCurrentIsoWeekYear';
import { routeBuilders } from '../../../routes';
import type { PotSummary } from '../types/pot.types';

type PanelType = 'deadlines' | 'help';

export default function PotApprovalsPage() {
  const navigate = useNavigate();
  const [yearsToLoad, setYearsToLoad] = useState(3);

  // Local panel state for deadlines and help (not URL-based)
  const [localPanel, setLocalPanel] = useState<PanelType | null>(null);

  // Snackbar state for move to active period feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // 1. URL state — period selection (shareable, bookmarkable)
  const { selectedPeriodId: urlPeriodId, setPeriodId } = usePotPeriodState();

  // 1a. Local period state for immediate updates (URL updates are async)
  const [localPeriodId, setLocalPeriodId] = useState<string | null>(null);
  const selectedPeriodId = localPeriodId ?? urlPeriodId;

  useEffect(() => {
    if (urlPeriodId && urlPeriodId !== localPeriodId) {
      setLocalPeriodId(urlPeriodId);
    }
  }, [urlPeriodId, localPeriodId]);

  const handlePeriodChange = useCallback(
    (periodId: string) => {
      setLocalPeriodId(periodId);
      setPeriodId(periodId);
    },
    [setPeriodId]
  );

  // 2. Zustand state — search text (ephemeral UI state)
  const { searchText, setSearchText } = usePotApprovalsUIStore(
    useShallow(state => ({
      searchText: state.searchText,
      setSearchText: state.setSearchText,
    }))
  );

  // 3. Debounce search (300ms for API calls)
  const debouncedSearchText = useDebounce(searchText, 300);

  // 4. Current ISO week year (for DeadlinesPanel)
  const { currentIsoYear } = useCurrentIsoWeekYear();

  // 5. Fetch period data
  const { data: periodData } = useGetPotPeriods({ yearsToLoad });

  const handleLoadMoreYears = useCallback(() => {
    setYearsToLoad(prev => prev + 3);
  }, []);

  // 6. Period navigation hook
  const {
    effectivePeriodId,
    validatedPeriodId,
    handlePrev,
    handleNext,
    canGoPrev,
    canGoNext,
    periodsByYear,
    selectedPeriodLabel,
    selectedPeriodShortLabel,
  } = usePeriodNavigation({
    periodData,
    selectedPeriodId,
    onPeriodChange: handlePeriodChange,
  });

  // 7. Fetch pots data with infinite scroll — use validatedPeriodId to avoid
  // firing requests with stale/invalid period IDs that would return 404.
  const {
    pots: apiPots,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
  } = useGetPots(validatedPeriodId, debouncedSearchText);

  // 7a. Move to active period mutation
  const { mutate: movePotToActivePeriodMutate } = useMovePotToActivePeriod();

  // 7b. Move to primary handler
  const handleMoveToPrimary = useMoveToPrimaryHandler(setSnackbar);

  // 7c. Extend submit deadline — the hook owns the confirmation dialog and copy
  const { open: openExtendDeadline, dialog: extendDeadlineDialog } =
    useExtendDeadlineAction();

  // 8. Transform API DTOs to display format
  const pots = useMemo<PotSummary[]>(() => {
    const selectedPeriod = periodData?.periods.find(
      p => p.id === selectedPeriodId
    );

    if (!selectedPeriod) {
      return [];
    }

    return apiPots.map(dto =>
      convertToTablePot(dto, {
        year: selectedPeriod.year,
        weekNumber: selectedPeriod.weekNumber,
        start: selectedPeriod.periodStart,
        end: selectedPeriod.periodEnd,
      })
    );
  }, [apiPots, periodData, selectedPeriodId]);

  const handleExtendDeadline = useCallback(
    (potId: string) => {
      const pot = pots.find(p => p.id === potId);
      if (pot) {
        openExtendDeadline(potId, pot.period.weekNumber);
      }
    },
    [pots, openExtendDeadline]
  );

  // 9. Group data state
  const data = useMemo(
    () => ({
      pots,
      totalCount,
      hasMore,
      isLoading,
      isLoadingMore,
      error,
    }),
    [pots, totalCount, hasMore, isLoading, isLoadingMore, error]
  );

  // 10. Panel management
  const isPanelOpen = localPanel !== null;

  const handleDeadlinesClick = () => {
    setLocalPanel('deadlines');
  };

  const handlePotClick = (clickedPotId: string) => {
    navigate(routeBuilders.finance.potDetail(clickedPotId));
  };

  const handleHelpClick = () => {
    setLocalPanel('help');
  };

  const handleCloseLocalPanel = () => {
    setLocalPanel(null);
  };

  const handleMoveToActivePeriod = useCallback(
    (movePotId: string) => {
      movePotToActivePeriodMutate(
        { potId: movePotId },
        {
          onSuccess: response => {
            setSnackbar({
              open: true,
              message: `Pot moved from Week ${response.fromPeriod.weekNumber} to Week ${response.toPeriod.weekNumber}`,
              severity: 'success',
            });
          },
          onError: e => {
            setSnackbar({
              open: true,
              message: getErrorMessage(e),
              severity: 'error',
            });
          },
        }
      );
    },
    [movePotToActivePeriodMutate]
  );

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // 11. Reset store on unmount
  useEffect(() => {
    return () => {
      usePotApprovalsUIStore.getState().reset();
    };
  }, []);

  const panelContent = useMemo(() => {
    if (localPanel === 'deadlines') {
      return (
        <>
          <ResizableHandle storageKey="deadlines-panel" />
          <SidePanel
            open
            onClose={handleCloseLocalPanel}
            title="Deadlines"
            testId="deadlines-panel"
            storageKey="deadlines-panel"
          >
            <DeadlinesPanel currentYear={currentIsoYear} />
          </SidePanel>
        </>
      );
    }

    if (localPanel === 'help') {
      return (
        <>
          <ResizableHandle />
          <SidePanel
            open
            onClose={handleCloseLocalPanel}
            title="Help"
            testId="pot-approvals-help-drawer"
          >
            <HelpPanel helpContent={POT_APPROVALS_HELP} />
          </SidePanel>
        </>
      );
    }

    return undefined;
  }, [localPanel, currentIsoYear]);

  return (
    <PermissionGate permission={Permissions.Pots.All.Read}>
      <PageContentArea isPanelOpen={isPanelOpen} panel={panelContent}>
        <PageHeader
          title="Pot Approvals"
          helpAction={{
            onClick: handleHelpClick,
            testId: 'pot-approvals-help-button',
          }}
          actions={
            <PageHeaderAction
              label="Deadlines"
              onClick={handleDeadlinesClick}
              icon={<CalendarMonthIcon />}
              data-testid="deadlines-button"
            />
          }
          data-testid="pot-approvals-header"
        />

        <PageToolbar
          actions={<PotGroupingMenu />}
          tabs={
            <PeriodSelector
              label={selectedPeriodLabel}
              shortLabel={selectedPeriodShortLabel}
              periodsByYear={periodsByYear}
              selection={{
                effectivePeriodId,
                onPeriodChange: handlePeriodChange,
              }}
              navigation={{
                onPrev: handlePrev,
                onNext: handleNext,
                canGoPrev,
                canGoNext,
              }}
              pagination={{
                hasMoreYears: periodData?.hasMoreYears,
                onLoadMoreYears: handleLoadMoreYears,
              }}
            />
          }
          searchValue={searchText}
          onSearchChange={setSearchText}
          data-testid="pot-approvals-toolbar"
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <PotApprovalsListView
            data={data}
            onLoadMore={loadMore}
            onPotClick={handlePotClick}
            onMoveToActivePeriod={handleMoveToActivePeriod}
            onMoveToPrimary={handleMoveToPrimary}
            onExtendDeadline={handleExtendDeadline}
            searchText={searchText}
          />
        </div>

        <PageSnackbar snackbar={snackbar} onClose={handleCloseSnackbar} />

        {extendDeadlineDialog}
      </PageContentArea>
    </PermissionGate>
  );
}
